import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthState } from '@core/store/auth/auth.state';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, filter, map, Observable, of, take } from 'rxjs';

export type OneOfScopesConfigPartial = {
  oneOf: string[];
};

export type AllOfScopesConfigPartial = {
  allOf: string[];
};

export type AndScopesConfigPartial =
  | {
      and: [OneOfScopesConfigPartial, OneOfScopesConfigPartial];
    }
  | {
      and: [AllOfScopesConfigPartial, AllOfScopesConfigPartial];
    }
  | {
      and: [OneOfScopesConfigPartial, AllOfScopesConfigPartial];
    }
  | {
      and: [AllOfScopesConfigPartial, OneOfScopesConfigPartial];
    };

export type OrScopesConfigPartial =
  | {
      or: [OneOfScopesConfigPartial, OneOfScopesConfigPartial];
    }
  | {
      or: [AllOfScopesConfigPartial, AllOfScopesConfigPartial];
    }
  | {
      or: [OneOfScopesConfigPartial, AllOfScopesConfigPartial];
    }
  | {
      or: [AllOfScopesConfigPartial, OneOfScopesConfigPartial];
    };

export type RouteScopeConfig =
  | OneOfScopesConfigPartial
  | AllOfScopesConfigPartial
  | AndScopesConfigPartial
  | OrScopesConfigPartial;

const matchScopes = (userScope: string, routeScope: string): boolean => {
  const [userPrefix, userAction] = userScope.split(':');
  const [routePrefix, routeAction] = routeScope.split(':');
  return userPrefix === routePrefix && (userAction === 'any' || routeAction === 'any' || userAction === routeAction);
};

export const isActionValidSystemWide = (action: string, usersSystemWideAllowedActions: string[]) => {
  // MAKING SURE THAT SUPER ACTION IS NOT VALID SYSTEM WIDE
  return (
    action !== 'super' &&
    (usersSystemWideAllowedActions.includes(action) || usersSystemWideAllowedActions.includes('any'))
  );
};

export const checkOneOfScopes = (userScopes: string[], config: OneOfScopesConfigPartial): boolean => {
  return config.oneOf.some(routeScope => (userScopes || []).some(userScope => matchScopes(userScope, routeScope)));
};

export const checkAllOfScopes = (userScopes: string[], config: AllOfScopesConfigPartial): boolean => {
  return config.allOf.every(routeScope => (userScopes || []).some(userScope => matchScopes(userScope, routeScope)));
};

const checkAndScopes = (userScopes: string[], config: AndScopesConfigPartial): boolean => {
  return config.and.every(scopeConfig => {
    if ('oneOf' in scopeConfig) {
      return checkOneOfScopes(userScopes, scopeConfig);
    } else if ('allOf' in scopeConfig) {
      return checkAllOfScopes(userScopes, scopeConfig);
    }
    return false;
  });
};

const checkOrScopes = (userScopes: string[], config: OrScopesConfigPartial): boolean => {
  return config.or.some(scopeConfig => {
    if ('oneOf' in scopeConfig) {
      return checkOneOfScopes(userScopes, scopeConfig);
    } else if ('allOf' in scopeConfig) {
      return checkAllOfScopes(userScopes, scopeConfig);
    }
    return false;
  });
};

// Original synchronous function (kept for reference)
// export function checkScopes(routeScopes: RouteScopeConfig) {
//   const store = inject(Store);
//   const userScopes = store.selectSnapshot(AuthState.getUserScopes);
//
//   if (!routeScopes) {
//     return true; // If no scopes are specified, allow access
//   }
//
//   if ('oneOf' in routeScopes) {
//     return checkOneOfScopes(userScopes, routeScopes);
//   } else if ('allOf' in routeScopes) {
//     return checkAllOfScopes(userScopes, routeScopes);
//   } else if ('and' in routeScopes) {
//     return checkAndScopes(userScopes, routeScopes);
//   } else if ('or' in routeScopes) {
//     return checkOrScopes(userScopes, routeScopes);
//   }
//
//   return false;
// }

export function checkScopes(routeScopes: RouteScopeConfig): Observable<boolean> {
  const store = inject(Store);

  if (!routeScopes) {
    return of(true); // If no scopes are specified, allow access
  }

  /**
   * When using filter() with take(1), the observable will only emit when the filter condition is met.
   * If scopes are not yet available, the filter will prevent emission, and the guard will wait.
   *
   * Angular's router guard system handles this correctly:
   * - If the guard returns an Observable, Angular will wait for it to emit before deciding
   * - If the Observable never emits (because scopes never become available), the route won't activate
   * - This effectively blocks navigation until scopes are available and properly checked
   *
   * No need to explicitly return false when scopes aren't available, as the navigation
   * will be pending until the Observable emits or completes.
   */
  return store.select(AuthState.getUserScopes).pipe(
    filter(userScopes => Array.isArray(userScopes) && userScopes.length > 0),
    map(userScopes => {
      if ('oneOf' in routeScopes) {
        return checkOneOfScopes(userScopes, routeScopes);
      } else if ('allOf' in routeScopes) {
        return checkAllOfScopes(userScopes, routeScopes);
      } else if ('and' in routeScopes) {
        return checkAndScopes(userScopes, routeScopes);
      } else if ('or' in routeScopes) {
        return checkOrScopes(userScopes, routeScopes);
      }
      return false;
    }),
    take(1)
  );
}

export function checkScopesReactive(routeScopes: RouteScopeConfig) {
  const store = inject(Store);
  return store.select(AuthState.getUserScopes).pipe(
    filter(userScopes => userScopes && userScopes.length > 0),
    map(userScopes => {
      if (!routeScopes) {
        return true; // If no scopes are specified, allow access
      }

      if ('oneOf' in routeScopes) {
        return checkOneOfScopes(userScopes, routeScopes);
      } else if ('allOf' in routeScopes) {
        return checkAllOfScopes(userScopes, routeScopes);
      } else if ('and' in routeScopes) {
        return checkAndScopes(userScopes, routeScopes);
      } else if ('or' in routeScopes) {
        return checkOrScopes(userScopes, routeScopes);
      }

      return false;
    }),
    distinctUntilChanged()
  );
}

// Original synchronous guard (kept for reference)
// export const authGuard: CanActivateFn = route => {
//   const notificationService = inject(NotificationService);
//
//   const routeScopes = route.data['scopes'] as RouteScopeConfig;
//
//   console.log(`Checking scopes for route: ${route.url} with scopes:`, routeScopes);
//   const result = checkScopes(routeScopes);
//   if (!result) {
//     notificationService.showError('You do not have access to this page, please contact your administrator.');
//   }
//   return result;
// };

export const authGuard: CanActivateFn = route => {
  const router = inject(Router);
  const routeScopes = route.data['scopes'] as RouteScopeConfig;

  return checkScopes(routeScopes).pipe(
    map((result): boolean | UrlTree => {
      if (!result) {
        return router.createUrlTree(['/permission-denied']);
      }
      return result;
    })
  );
};
